import { useQuery, gql } from "@apollo/client";
import { useParams } from "react-router-dom";
import type { BrandSettlementQuery, BrandSettlementQueryVariables } from "../../../../__generated__/graphql";

const GET_CURRENT_BRAND_NAME = gql`
  query SettlementDetailCurrentBrandName {
    currentBrand {
      id
      name
    }
  }
`;

const GET_BRAND_SETTLEMENT = gql`
  query BrandSettlement($settlementId: Int!) {
    brandSettlement(settlementId: $settlementId) {
      id
      settlementTitle
      finalAmountVatExcluded
      executionCostVatExcluded
      otherCostVatExcluded
      executionCostTotalVatExcluded
      taxInvoiceWrittenAt
      brand {
        id
        name
      }
      lines {
        id
        channel
        productName
        startedAt
        endedAt
        paybackAmount
        operationFeeAmount
        actualSettlementCount
        settlementAmount
      }
      otherCosts {
        id
        name
        amount
      }
      taxInvoice {
        businessName
        businessNumber
        representativeName
        address
        contactNumber
        businessType
      }
    }
  }
`;

const SettlementDetail = () => {
  const { settlementId } = useParams<{ settlementId: string }>();
  const id = Number(settlementId || "0");
  const { data: brandData } = useQuery(GET_CURRENT_BRAND_NAME);
  const { data: settlementData, loading: settlementLoading, error: settlementError } = useQuery<
    BrandSettlementQuery,
    BrandSettlementQueryVariables
  >(GET_BRAND_SETTLEMENT, {
    variables: { settlementId: id },
    skip: !id,
  });

  const settlement = settlementData?.brandSettlement;
  const brandName = brandData?.currentBrand?.name ?? settlement?.brand?.name ?? "-";

  if (settlementLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (settlementError || !settlement) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-500">
          {settlementError ? `에러: ${settlementError.message}` : "정산 내역을 찾을 수 없습니다."}
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDateTime24h = (dateString: string) => {
    const d = new Date(dateString);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
  };

  const formatChannel = (channel: string) => {
    const channelMap: Record<string, string> = {
      NAVER: "네이버",
      CARROT: "당근마켓",
      COUPANG: "쿠팡",
    };
    return channelMap[channel] || channel;
  };

  const totalPayback = settlement.lines.reduce(
    (sum: number, l) => sum + parseFloat(l.paybackAmount),
    0
  );
  const totalOperationFee = settlement.lines.reduce(
    (sum: number, l) => sum + parseFloat(l.operationFeeAmount),
    0
  );
  const totalActualCount = settlement.lines.reduce(
    (sum: number, l) => sum + l.actualSettlementCount,
    0
  );
  const totalSettlementAmount = settlement.lines.reduce(
    (sum: number, l) => sum + parseFloat(l.settlementAmount),
    0
  );

  return (
    <div className="space-y-8">
      <div>
        <div className="text-2xl font-bold text-gray-900">{settlement.settlementTitle}</div>
        <div className="mt-1 text-sm text-gray-500">브랜드 계정: {brandName}</div>
      </div>

      <div className="space-y-4">
        <div className="text-lg font-bold text-gray-900">
          I. 정산 결과 <span className="text-sm font-medium text-gray-500">(vat 별도)</span>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-gray-200">
            <div className="p-4">
              <div className="text-xs font-medium text-gray-500 mb-1">최종 금액</div>
              <div className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
                {parseFloat(settlement.finalAmountVatExcluded).toLocaleString()}
                <span className="text-2xl font-bold ml-1 text-gray-500">원</span>
              </div>
            </div>
            <div className="p-4">
              <div className="text-xs font-medium text-gray-500 mb-1">집행 비용</div>
              <div className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
                {parseFloat(settlement.executionCostVatExcluded).toLocaleString()}
                <span className="text-2xl font-bold ml-1 text-gray-500">원</span>
              </div>
            </div>
            <div className="p-4">
              <div className="text-xs font-medium text-gray-500 mb-1">기타 비용</div>
              <div className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
                {parseFloat(settlement.otherCostVatExcluded).toLocaleString()}
                <span className="text-2xl font-bold ml-1 text-gray-500">원</span>
              </div>
            </div>
            <div className="p-4">
              <div className="text-xs font-medium text-gray-500 mb-1">세금 계산서 작성 일자</div>
              <div className="text-sm font-semibold text-gray-900">{formatDate(settlement.taxInvoiceWrittenAt)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="text-lg font-bold text-gray-900">
          집행 비용 <span className="text-sm font-medium text-gray-500">(vat 별도)</span>
        </div>
        <div className="text-sm text-gray-700">
          <span className="font-semibold">집행 비용 총:</span>{" "}
          {parseFloat(settlement.executionCostTotalVatExcluded).toLocaleString()}원{" "}
          <span className="text-gray-500">(vat 별도)</span>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    채널
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    제품
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    진행일시(24h)
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    페이백금액
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    운영수수료
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    실정산건수
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    정산금액
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {settlement.lines.map((line) => (
                  <tr key={line.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{formatChannel(line.channel)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{line.productName}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-700">
                        {formatDateTime24h(line.startedAt)} ~ {formatDateTime24h(line.endedAt)}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="text-sm text-gray-900">
                        {parseFloat(line.paybackAmount).toLocaleString()}원
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="text-sm text-gray-900">
                        {parseFloat(line.operationFeeAmount).toLocaleString()}원
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="text-sm text-gray-900">{line.actualSettlementCount.toLocaleString()}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="text-sm font-semibold text-gray-900">
                        {parseFloat(line.settlementAmount).toLocaleString()}원
                      </div>
                    </td>
                  </tr>
                ))}

                <tr className="bg-gray-50">
                  <td className="px-4 py-3 text-sm font-semibold text-gray-700">TOTAL</td>
                  <td className="px-4 py-3 text-sm text-gray-500">-</td>
                  <td className="px-4 py-3 text-sm text-gray-500">-</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-700 text-right">
                    {totalPayback.toLocaleString()}원
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-700 text-right">
                    {totalOperationFee.toLocaleString()}원
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-700 text-right">
                    {totalActualCount.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                    {totalSettlementAmount.toLocaleString()}원
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 기타비용 섹션 */}
      {settlement.otherCosts && settlement.otherCosts.length > 0 && (
        <div className="space-y-3">
          <div className="text-lg font-bold text-gray-900">
            기타비용 <span className="text-sm font-medium text-gray-500">(vat 별도)</span>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      항목명
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      금액
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {settlement.otherCosts.map((cost) => (
                    <tr key={cost.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{cost.name}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-900">
                          {parseFloat(cost.amount).toLocaleString()}원
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50">
                    <td className="px-4 py-3 text-sm font-semibold text-gray-700">합계</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                      {parseFloat(settlement.otherCostVatExcluded).toLocaleString()}원
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 전자 세금 계산서 발행 섹션 */}
      {settlement.taxInvoice && (
        <div className="space-y-3">
          <div className="text-lg font-bold text-gray-900">II. 전자 세금 계산서 발행</div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      작성일자
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      회사명
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      사업자등록번호
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      합계금액
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      공급가액
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      세액
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      비고
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(settlement.taxInvoiceWrittenAt)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{settlement.taxInvoice.businessName}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{settlement.taxInvoice.businessNumber}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="text-sm font-semibold text-gray-900">
                        {Math.round(parseFloat(settlement.finalAmountVatExcluded) * 1.1).toLocaleString()}원
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="text-sm text-gray-900">
                        {parseFloat(settlement.finalAmountVatExcluded).toLocaleString()}원
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <div className="text-sm text-gray-900">
                        {Math.round(parseFloat(settlement.finalAmountVatExcluded) * 0.1).toLocaleString()}원
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm text-gray-500">
                        {settlement.taxInvoice.businessType || "-"}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettlementDetail;
